import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { setupAuthGuards } from '@shared/utils/route-guards'
import { setupChunkErrorHandler } from '@shared/utils/chunk-error-handler'

const Layout = () => import('../layouts/MainLayout.vue')
// P1: 商户端仪表盘（参照企业端风格）
const DashboardView = () => import('../views/dashboard/DashboardView.vue')
// M7: 商家主页（基本信息+资质+服务企业+佣金）
const MerchantHomeView = () => import('../views/merchant-home/MerchantHomeView.vue')
// 排期管理（M4新增 - 完整版）
const ScheduleManagementPage = () => import('../pages/schedule/ScheduleManagementPage.vue')
const ScheduleEditPage = () => import('../pages/schedule/ScheduleEditPage.vue')
const ScheduleDetailPage = () => import('../pages/schedule/ScheduleDetailPage.vue')
// M5-Phase5: 扫码收款（商家扫员工码）
const ScanPaymentPage = () => import('../pages/scan-payment/ScanPaymentPage.vue')
// 旧页面 import 已清理（MySchedulesPage / SchedulesListView / ScheduleEditor）
const DishesListView = () => import('../views/dishes/DishListView.vue')
const DishEditorView = () => import('../views/dishes/DishEditorView.vue')
// 旧页面 import 已清理（OrdersListView / FinanceCenterView）
// P1-2: 商户端订单中心（Tab式布局）
const OrderManagementView = () => import('../views/orders/OrderManagementView.vue')
// B2B预订单管理 - 商户端出单中心
const MerchantOrderCenterView = () => import('../views/orders/center/MerchantOrderCenterView.vue')
const SuppliersPlaceholder = () => import('../views/HomeView.vue')
// P1-3: 商户端财务中心（Tab式布局）
const FinanceManagementView = () => import('../views/finance/FinanceManagementView.vue')
const ReportsOverviewView = () => import('../views/reports/ReportsOverviewView.vue')
const DeliveryPlanView = () => import('../views/delivery/DeliveryPlanView.vue')
const AccountManagementView = () => import('../views/account/AccountManagement.vue')
const LoginView = () => import('../views/LoginView.vue')
const ForbiddenView = () => import('../views/error/ForbiddenView.vue')
const NotFoundView = () => import('../views/error/NotFoundView.vue')
const AccessGuidanceView = () => import('../views/error/AccessGuidanceView.vue')

const routes: RouteRecordRaw[] = [
  { path: '/login', name: 'login', component: LoginView, meta: { title: '登录', requiresAuth: false } },
  {
    path: '/',
    component: Layout,
    meta: { requiresAuth: true, roles: ['merchant'] },
    children: [
      {
        path: '',
        name: 'home',
        component: DashboardView,
        meta: {
          title: '首页',
          requiresAuth: true,
          roles: ['merchant'],
          menuId: 'dashboard',
          icon: 'House',
          sort: 1,
          breadcrumbs: [
            { title: '首页', path: '/' }
          ]
        }
      }
      ,
      { path: 'dashboard', name: 'dashboard', component: DashboardView, meta: {
        title: '仪表盘', roles: ['merchant'], menuId: 'dashboard', icon: 'House', sort: 1,
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '仪表盘' } ]
      } },
      { path: 'merchant', name: 'merchant-home', component: MerchantHomeView, meta: {
        title: '商家主页', roles: ['merchant'], menuId: 'merchant-home', icon: 'UserFilled', sort: 2,
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '商家主页' } ]
      } },
      // M4排期管理（完整版 - 周视图）
      { path: 'schedules', name: 'schedules', component: ScheduleManagementPage, meta: {
        title: '排期管理', roles: ['merchant'], menuId: 'schedules', icon: 'Calendar', sort: 3,
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '排期与菜单', path: '/schedules' }, { title: '排期管理' } ]
      } },
      { path: 'schedules/create/:workplaceId/:mealSegment', name: 'schedule-create', component: ScheduleEditPage, meta: {
        title: '创建排期', roles: ['merchant'], menuId: 'schedules',
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '排期与菜单', path: '/schedules' }, { title: '创建排期' } ]
      } },
      { path: 'schedules/edit/:workplaceId/:mealSegment/:scheduleId', name: 'schedule-edit-new', component: ScheduleEditPage, meta: {
        title: '编辑排期', roles: ['merchant'], menuId: 'schedules',
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '排期与菜单', path: '/schedules' }, { title: '编辑排期' } ]
      } },
      { path: 'schedules/detail/:scheduleId', name: 'schedule-detail', component: ScheduleDetailPage, meta: {
        title: '排期详情', roles: ['merchant'], menuId: 'schedules',
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '排期与菜单', path: '/schedules' }, { title: '排期详情' } ]
      } },
      // ⚠️ 旧版排期路由（schedules-old、schedule-edit via ScheduleEditor）已清理 — 功能整合到 ScheduleManagementPage
      { path: 'dishes', name: 'dishes', component: DishesListView, meta: {
        title: '我的菜品', roles: ['merchant'], menuId: 'dishes', icon: 'Dish', sort: 4,
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '排期与菜单', path: '/schedules' }, { title: '我的菜品' } ]
      } },
      { path: 'dishes/create', name: 'dish-create', component: DishEditorView, meta: {
        title: '新建菜品', roles: ['merchant'], menuId: 'dishes',
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '排期与菜单', path: '/schedules' }, { title: '我的菜品', path: '/dishes' }, { title: '新建' } ]
      } },
      { path: 'dishes/:id', name: 'dish-detail',
        // ⚠️ 详情抽屉是 Teleport 组件（el-drawer）。若作为独立路由页渲染，RouterView 的 Transition 可能在卸载时触发 parentNode=null 崩溃，导致返回列表白屏。
        // 解决：保持在“列表页”内渲染抽屉，通过 query.detail 控制显示，详情路由仅作为深链接入口重定向到列表。
        redirect: (to) => ({ name: 'dishes', query: { ...(to.query || {}), detail: String(to.params.id || '') } }),
        meta: {
        title: '菜品详情', roles: ['merchant'], menuId: 'dishes',
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '排期与菜单', path: '/schedules' }, { title: '我的菜品', path: '/dishes' }, { title: '详情' } ]
      } },
      { path: 'dishes/:id/edit', name: 'dish-edit', component: DishEditorView, meta: {
        title: '编辑菜品', roles: ['merchant'], menuId: 'dishes',
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '排期与菜单', path: '/schedules' }, { title: '我的菜品', path: '/dishes' }, { title: '编辑' } ]
      } },
      { path: 'orders', name: 'orders', component: OrderManagementView, meta: {
        title: '订单管理', roles: ['merchant'], menuId: 'orders', icon: 'Tickets', sort: 8,
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '订单中心' }, { title: '订单管理' } ]
      } },
      // B2B预订单管理 - 商户端出单中心
      { path: 'orders/center', name: 'order-center', component: MerchantOrderCenterView, meta: {
        title: '出单中心', roles: ['merchant'], menuId: 'order-center',
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '订单中心' }, { title: '出单中心' } ]
      } },
      // ⚠️ 旧版订单列表（orders-old）已清理 — 功能整合到 OrderManagementView
      // M5-Phase5: 扫码收款
      { path: 'scan-payment', name: 'scan-payment', component: ScanPaymentPage, meta: {
        title: '扫码收款', roles: ['merchant'], menuId: 'scan-payment', icon: 'Scan', sort: 7.5,
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '扫码收款' } ]
      } },
      { path: 'suppliers', name: 'suppliers', component: SuppliersPlaceholder, meta: {
        title: '供应商', roles: ['merchant'], menuId: 'suppliers', icon: 'Goods', sort: 9,
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '供应商' } ]
      } },
      { path: 'finance', name: 'finance', component: FinanceManagementView, meta: {
        title: '财务中心', roles: ['merchant'], menuId: 'finance', icon: 'Coin', sort: 10,
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '财务中心' } ]
      } },
      // ⚠️ 旧版财务中心（finance-old）已清理 — 功能整合到 FinanceManagementView
      { path: 'reports', name: 'reports', component: ReportsOverviewView, meta: {
        title: '统计分析', roles: ['merchant'], menuId: 'reports', icon: 'DataLine', sort: 11,
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '统计分析' } ]
      } },
      { path: 'delivery', name: 'delivery', component: DeliveryPlanView, meta: {
        title: '配送计划', roles: ['merchant'], menuId: 'delivery', icon: 'Van', sort: 12,
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '配送计划' } ]
      } },
      { path: 'account', name: 'account', component: AccountManagementView, meta: {
        title: '账号管理', roles: ['merchant'], menuId: 'account', icon: 'Setting', sort: 13,
        breadcrumbs: [ { title: '首页', path: '/' }, { title: '账号管理' } ]
      } }
    ]
  },
  { path: '/403', name: 'forbidden', component: ForbiddenView, meta: { title: '无权访问', requiresAuth: false } },
  { path: '/access-guidance', name: 'access-guidance', component: AccessGuidanceView, meta: { title: '访问指引', requiresAuth: false } },
  { path: '/404', name: 'not-found', component: NotFoundView, meta: { title: '页面不存在', requiresAuth: false } },
  { path: '/:pathMatch(.*)*', redirect: '/404' }
]

const router = createRouter({
  // 为商户后台设置基础路径，便于部署到 /merchant-admin/
  history: createWebHistory('/merchant-admin/'),
  routes
})

// 调试日志已收敛：不再输出路由心跳日志

// 启用共享的认证路由守卫（允许商户与运营商/超管）
// 关键修复：为商户端关闭"在登录页自动跳转首页"的逻辑，确保始终显示登录页
setupAuthGuards(router, {
  loginPath: '/login',
  homePath: '/',
  requireRoles: ['merchant'],
  excludeRoutes: [],
  guidancePath: '/access-guidance'
})

// ⭐ 设置 chunk 加载失败自动恢复（部署新版本后，旧 chunk 404 时自动刷新）
setupChunkErrorHandler(router)

export default router